import styled from 'styled-components';
import SegmentedToggle from '../primitives/SegmentedToggle';
import { BodyText, CardTitle, MutedText } from '../primitives/Typography';
import { BillingInterval, PlanId } from 'screening/types';
import {
  PlanCatalogEntry,
  RegionCatalog,
  formatMoney,
  planAmount,
  processorCopy,
} from 'screening/billing/catalog';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button<{ $selected: boolean }>`
  text-align: left;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--screening-navy)' : 'var(--screening-hairline)'};
  border-radius: var(--screening-r-card);
  background: var(--screening-paper);
  padding: 22px 20px 20px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: none;
  transition: border-color 160ms ease;

  &:hover {
    border-color: var(--screening-navy);
  }

  &:focus-visible {
    outline: 2px solid var(--screening-navy);
    outline-offset: 2px;
  }
`;

const CardHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PriceBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const PlanPrice = styled.p`
  margin: 0;
  font-family: var(--screening-font-display);
  font-size: 30px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--screening-navy);
  font-feature-settings:
    'tnum' 1,
    'lnum' 1;
`;

const Volume = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  li {
    font-size: 14px;
    line-height: 21px;
    color: var(--screening-muted);
  }
`;

interface PlanPickerProps {
  catalog: RegionCatalog;
  interval: BillingInterval;
  selected: PlanId;
  onInterval: (interval: BillingInterval) => void;
  onSelect: (planId: PlanId) => void;
}

const PlanPicker = ({
  catalog,
  interval,
  selected,
  onInterval,
  onSelect,
}: PlanPickerProps) => {
  const unitLabel = interval === 'annual' ? 'billed yearly' : 'billed monthly';

  return (
    <>
      <Toolbar>
        <SegmentedToggle
          options={[
            { value: 'monthly', label: 'Monthly' },
            { value: 'annual', label: 'Annual' },
          ]}
          value={interval}
          onChange={onInterval}
        />
        <MutedText style={{ fontSize: 13 }}>{processorCopy(catalog)}</MutedText>
      </Toolbar>
      <Grid>
        {catalog.plans.map((plan: PlanCatalogEntry) => {
          const amount = planAmount(plan, interval);
          return (
            <Card
              key={plan.id}
              type="button"
              $selected={selected === plan.id}
              onClick={() => onSelect(plan.id)}
              aria-pressed={selected === plan.id}
            >
              <CardHead>
                <CardTitle>{plan.name}</CardTitle>
                <MutedText style={{ fontSize: 13 }}>
                  {plan.recruiterRange}
                </MutedText>
              </CardHead>
              <PriceBlock>
                <PlanPrice className="screening-tnum">
                  {formatMoney(amount, catalog.currency)}
                </PlanPrice>
                <MutedText style={{ fontSize: 13 }}>{unitLabel}</MutedText>
              </PriceBlock>
              <BodyText style={{ fontSize: 14, lineHeight: '21px' }}>
                {plan.blurb}
              </BodyText>
              <Volume>
                <li className="screening-tnum">
                  {plan.voiceInterviews.toLocaleString()} voice interviews.
                </li>
                <li className="screening-tnum">
                  {plan.applicationScores.toLocaleString()} applications scored.
                </li>
                {plan.extras.map((extra) => (
                  <li key={extra}>{extra}</li>
                ))}
              </Volume>
            </Card>
          );
        })}
      </Grid>
    </>
  );
};

export default PlanPicker;
